import React, { useState } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

// Quiet text actions shared by the empty Chat and Work composers. Wrapping
// keeps every suggestion available at narrow widths without a scrolling rail.
export default function StarterSuggestions({ items, onSelect, hasFiles = false, mode = 'chat', accessibilityLabel }) {
  const { colors } = useTheme();
  const [focusedId, setFocusedId] = useState(null);

  return (
    <View style={styles.group} accessibilityLabel={accessibilityLabel}>
      {items.slice(0, 3).map((item) => (
        <Pressable
          key={item.id}
          accessibilityRole="button"
          accessibilityLabel={item.label}
          accessibilityHint={`Adds this suggestion to the ${mode === 'work' ? 'Work' : 'Chat'} composer`}
          onPress={() => onSelect?.(hasFiles && item.promptWithFiles ? item.promptWithFiles : item.prompt)}
          onFocus={() => setFocusedId(item.id)}
          onBlur={() => setFocusedId(null)}
          style={({ hovered, pressed }) => [
            styles.action,
            {
              backgroundColor: hovered || pressed ? colors.solid_color_tertiary : 'transparent',
              borderColor: focusedId === item.id ? colors.text_color_secondary : 'transparent',
              opacity: pressed ? 0.7 : 1,
            },
          ]}
        >
          <View style={styles.icon} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
            <Icon name={item.icon || (mode === 'work' ? 'file-lines' : 'comment')} size={13} color={colors.text_color_secondary} />
          </View>
          <Text size="s" color="secondary" numberOfLines={2} style={styles.label}>
            {item.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  group: { width: '100%', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 8, rowGap: 0 },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    maxWidth: '100%',
    minHeight: 40,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 9,
    paddingVertical: 8,
    gap: 7,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 120ms ease, border-color 120ms ease' } }),
  },
  icon: { width: 13, height: 13, alignItems: 'center', justifyContent: 'center' },
  label: { lineHeight: 20, flexShrink: 1 },
});
